import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { CompanionNumber, CompanionService } from '@services/companion.service';
import { SecureStorageService } from '@services/secure-storage.service';

/**
 * Campaign Companion (WhatsApp) connection card for the profile/settings page.
 * Connection is via EMAIL OTP done inside WhatsApp: the user messages the LuQta
 * bot, replies with their account email, and enters the emailed code. This card
 * shows the how-to, the user's connected numbers, and lets them enable/disable or
 * unlink a number.
 */
@Component({
  selector: 'app-companion-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './companion-card.component.html',
  styleUrls: ['./companion-card.component.scss'],
})
export class CompanionCardComponent implements OnInit {
  private companion = inject(CompanionService);
  private modal = inject(NzModalService);
  private translateService = inject(TranslateService);
  private secureStorage = inject(SecureStorageService);

  numbers: CompanionNumber[] = [];
  loading = false;

  /** Account-level master switch, independent of the per-number toggles. */
  companionEnabled = false;
  enabledLoading = true;
  enabledSaving = false;

  /** The LuQta bot's WhatsApp number, shown as a wa.me link (digits only, no +). */
  botNumber = (environment as any).whatsappBotNumber || '';
  get botWaLink(): string {
    const n = (this.botNumber || '').replace(/[^0-9]/g, '');
    const text = encodeURIComponent('Hi LuQta, I want to connect my number.');
    return n ? `https://wa.me/${n}?text=${text}` : '';
  }

  ngOnInit(): void {
    const hasCompanionAccess = this.secureStorage.getItem<boolean>('hasCompanionAccess');
    if (hasCompanionAccess === false) {
      return;
    }
    this.loadCompanionEnabled();
    this.loadNumbers();
  }

  loadCompanionEnabled(): void {
    this.enabledLoading = true;
    this.companion.isCompanionEnabled().subscribe({
      next: (enabled) => {
        this.companionEnabled = enabled;
        this.enabledLoading = false;
      },
      error: () => {
        this.companionEnabled = false;
        this.enabledLoading = false;
      },
    });
  }

  toggleCompanionEnabled(): void {
    if (this.enabledSaving) return;

    const next = !this.companionEnabled;
    // Flip optimistically so the switch responds immediately, and roll back on failure.
    this.companionEnabled = next;
    this.enabledSaving = true;

    this.companion.setCompanionEnabled(next).subscribe({
      next: (enabled) => {
        this.companionEnabled = enabled;
        this.enabledSaving = false;
      },
      error: () => {
        this.companionEnabled = !next;
        this.enabledSaving = false;
      },
    });
  }

  loadNumbers(): void {
    this.loading = true;
    this.companion.listNumbers().subscribe({
      next: (rows) => {
        this.numbers = rows || [];
        this.loading = false;
      },
      error: () => {
        this.numbers = [];
        this.loading = false;
      },
    });
  }

  toggle(row: CompanionNumber): void {
    const next = !row.companion_enabled;
    this.companion.toggleNumber(row.id, next).subscribe({
      next: () => (row.companion_enabled = next),
      error: () => {},
    });
  }

  remove(row: CompanionNumber): void {
    this.modal.confirm({
      nzTitle: this.translateService.instant('companion.confirmDisconnectTitle'),
      nzContent: this.translateService.instant('companion.confirmDisconnectContent', { phone: row.phone_number }),
      nzOkText: this.translateService.instant('companion.confirmDisconnectOk'),
      nzOkDanger: true,
      nzOnOk: () => {
        this.companion.removeNumber(row.id).subscribe({
          next: () => (this.numbers = this.numbers.filter((n) => n.id !== row.id)),
          error: () => {},
        });
      },
    });
  }

  statusLabel(row: CompanionNumber): string {
    if (!row.is_verified) return this.translateService.instant('companion.statusNotVerified');
    return row.companion_enabled
      ? this.translateService.instant('companion.statusConnected')
      : this.translateService.instant('companion.disabled');
  }

  statusClass(row: CompanionNumber): string {
    if (!row.is_verified) return 'status-pending';
    return row.companion_enabled ? 'status-connected' : 'status-disabled';
  }
}
